<template>
  <v-dialog v-model="show" max-width="800px" @input="onVisibilityChange">
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ isEditing ? 'Edit Field' : 'Add Field' }}</span>
      </v-card-title>
      <v-card-text>
        <v-form ref="form" v-model="valid">
          <v-text-field
            v-model="field.name"
            label="Field Name"
            :rules="[v => !!v || 'Name is required']"
            required
          ></v-text-field>

          <v-text-field
            v-model="field.label"
            label="Field Label"
            :rules="[v => !!v || 'Label is required']"
            required
          ></v-text-field>

          <v-text-field
            v-model="field.fieldCode"
            label="Field Code"
            :rules="fieldCodeRules"
            :hint="fieldCodeHint"
            persistent-hint
            placeholder="e.g., staff_name, department_code"
          ></v-text-field>

          <v-textarea
            v-model="field.description"
            label="Description"
            rows="2"
          ></v-textarea>

          <v-textarea
            v-model="field.hint"
            label="Hint Text"
            rows="2"
          ></v-textarea>

          <v-select
            v-model="field.fieldType"
            :items="Object.values(FieldType)"
            label="Field Type"
            :rules="[v => !!v || 'Field type is required']"
            required
          ></v-select>

          <v-switch
            v-model="field.required"
            label="Required"
            color="primary"
          ></v-switch>

          <v-select
            v-model="field.role"
            :items="roles"
            item-text="label"
            item-value="value"
            label="Responsible Role"
            clearable
            hint="Who fills this field? Leave empty if the evaluatee fills it. Overrides section-level role."
            persistent-hint
          ></v-select>

          <!-- SECTION_SCORE field type selector -->
          <template v-if="field.fieldType === FieldType.SECTION_SCORE">
            <v-select
              v-model="field.scoreSectionId"
              :items="sections"
              item-text="name"
              item-value="id"
              label="Score Section"
              :rules="[v => !!v || 'Score section is required for SECTION_SCORE fields']"
              required
              hint="Select the section whose score this field will display"
              persistent-hint
            ></v-select>
          </template>

          <!-- PREDEFINED field type selector -->
          <template v-if="field.fieldType === FieldType.PREDEFINED">
            <v-select
              v-model="field.predefinedFieldType"
              :items="Object.values(PredefinedFieldType)"
              label="Predefined Field Type"
              :rules="[v => !!v || 'Predefined field type is required']"
              required
              hint="Select the user data to auto-populate"
              persistent-hint
            ></v-select>
          </template>

          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Reporting Options</div>

          <v-switch
            v-model="field.showInReport"
            label="Show in Report"
            color="primary"
            hint="Whether this field appears in generated reports"
            persistent-hint
          ></v-switch>

          <v-text-field
            v-if="field.showInReport"
            v-model.number="field.reportOrder"
            label="Report Order"
            type="number"
            hint="Display order in reports (lower numbers appear first)"
            persistent-hint
          ></v-text-field>

          <v-divider class="my-4"></v-divider>
          <div class="text-subtitle-2 mb-2">Scoring Options</div>

          <v-switch
            v-model="field.includeInScoring"
            label="Include in Scoring"
            color="primary"
            hint="Whether this field's score contributes to the total evaluation score"
            persistent-hint
            :disabled="field.fieldType === FieldType.PREDEFINED || field.fieldType === FieldType.SECTION_SCORE || field.useAggregate"
          ></v-switch>

          <v-text-field
            v-if="field.includeInScoring"
            v-model.number="field.maximumScore"
            label="Maximum Score"
            type="number"
            hint="Maximum possible score for percentage calculations (optional)"
            persistent-hint
          ></v-text-field>

          <v-switch
            v-model="field.useAggregate"
            label="Use Aggregate (Auto-select based on total score)"
            color="primary"
            hint="When enabled, this field auto-selects options based on total score from other fields"
            persistent-hint
            :disabled="field.fieldType === FieldType.PREDEFINED || field.fieldType === FieldType.SECTION_SCORE || ![FieldType.SINGLE_SELECT, FieldType.RADIO].includes(field.fieldType)"
          ></v-switch>

          <v-alert
            v-if="field.useAggregate"
            type="info"
            outlined
            dense
            class="mt-2"
          >
            Aggregate fields are READ-ONLY. Options will be auto-selected based on total score from scoring fields.
          </v-alert>

          <v-divider class="my-4"></v-divider>

          <!-- Field Type Specific Options -->
          <template v-if="field.fieldType === FieldType.TEXT || field.fieldType === FieldType.TEXTAREA">
            <v-text-field
              v-model.number="field.minLength"
              label="Minimum Length"
              type="number"
            ></v-text-field>
            <v-text-field
              v-model.number="field.maxLength"
              label="Maximum Length"
              type="number"
            ></v-text-field>
          </template>

          <template v-if="field.fieldType === FieldType.NUMBER">
            <v-text-field
              v-model.number="field.minValue"
              label="Minimum Value"
              type="number"
            ></v-text-field>
            <v-text-field
              v-model.number="field.maxValue"
              label="Maximum Value"
              type="number"
            ></v-text-field>
            <v-text-field
              v-model.number="field.stepValue"
              label="Step Value"
              type="number"
            ></v-text-field>
          </template>

          <template v-if="field.fieldType === FieldType.RATING">
            <v-text-field
              v-model.number="field.minRating"
              label="Minimum Rating"
              type="number"
            ></v-text-field>
            <v-text-field
              v-model.number="field.maxRating"
              label="Maximum Rating"
              type="number"
            ></v-text-field>
          </template>

          <template v-if="field.fieldType === FieldType.DATE">
            <v-text-field
              v-model="field.minDate"
              label="Minimum Date"
              type="date"
            ></v-text-field>
            <v-text-field
              v-model="field.maxDate"
              label="Maximum Date"
              type="date"
            ></v-text-field>
          </template>

          <template v-if="[FieldType.SINGLE_SELECT, FieldType.MULTI_SELECT, FieldType.CHECKBOX, FieldType.RADIO].includes(field.fieldType)">
            <div class="d-flex align-center mb-2">
              <div class="text-subtitle-1">Options</div>
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="addOption"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Option
              </v-btn>
            </div>
            <v-list>
              <v-list-item
                v-for="(option, index) in field.options"
                :key="index"
              >
                <v-list-item-content>
                  <div class="d-flex align-center flex-wrap">
                    <v-text-field
                      v-model="option.label"
                      label="Label"
                      class="mr-2"
                      style="flex: 1; min-width: 120px;"
                    ></v-text-field>
                    <v-text-field
                      v-model="option.value"
                      label="Value"
                      class="mr-2"
                      style="flex: 1; min-width: 100px;"
                    ></v-text-field>
                    <v-text-field
                      v-model.number="option.score"
                      label="Score"
                      type="number"
                      class="mr-2"
                      style="flex: 0.5; min-width: 80px;"
                    ></v-text-field>
                    <v-text-field
                      v-if="field.useAggregate"
                      v-model.number="option.requiredAggregate"
                      label="Required Aggregate"
                      type="number"
                      class="mr-2"
                      hint="Min total score to auto-select"
                      style="flex: 0.7; min-width: 120px;"
                    ></v-text-field>
                    <v-btn
                      icon
                      small
                      color="error"
                      @click="removeOption(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </template>

          <template v-if="field.fieldType === FieldType.MULTI_TEXT">
            <v-switch
              v-model="field.requiresLabels"
              label="Requires Labels"
              color="primary"
            ></v-switch>
            <v-text-field
              v-model="field.textEntriesPlaceholder"
              label="Text Entries Placeholder"
            ></v-text-field>
            <v-text-field
              v-model.number="field.minEntries"
              label="Minimum Entries"
              type="number"
            ></v-text-field>
            <v-text-field
              v-model.number="field.maxEntries"
              label="Maximum Entries"
              type="number"
            ></v-text-field>

            <template v-if="field.requiresLabels">
              <div class="d-flex align-center mb-2">
                <div class="text-subtitle-1">Text Fields</div>
                <v-spacer></v-spacer>
                <v-btn
                  text
                  color="primary"
                  @click="addTextField"
                >
                  <v-icon left>mdi-plus</v-icon>
                  Add Text Field
                </v-btn>
              </div>
              <v-list>
                <v-list-item
                  v-for="(textField, index) in field.textTemplates"
                  :key="index"
                >
                  <v-list-item-content>
                    <div class="d-flex align-center">
                      <v-text-field
                        v-model="textField.label"
                        label="Label"
                        class="mr-2"
                      ></v-text-field>
                      <v-text-field
                        v-model="textField.placeholder"
                        label="Placeholder"
                        class="mr-2"
                      ></v-text-field>
                      <v-switch
                        v-model="textField.required"
                        label="Required"
                        color="primary"
                        class="mr-2"
                      ></v-switch>
                      <v-btn
                        icon
                        small
                        color="error"
                        @click="removeTextField(index)"
                      >
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </div>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </template>
          </template>

          <!-- MULTI_FIELD type specific options -->
          <template v-if="field.fieldType === FieldType.MULTI_FIELD">
            <v-text-field
              v-model.number="field.minEntries"
              label="Minimum Entries"
              type="number"
              hint="Minimum number of entries required"
            ></v-text-field>
            <v-text-field
              v-model.number="field.maxEntries"
              label="Maximum Entries"
              type="number"
              hint="Maximum number of entries allowed"
            ></v-text-field>

            <div class="d-flex align-center my-4">
              <div class="text-subtitle-1">Sub Fields</div>
              <v-spacer></v-spacer>
              <v-btn
                text
                color="primary"
                @click="addSubField"
              >
                <v-icon left>mdi-plus</v-icon>
                Add Sub Field
              </v-btn>
            </div>

            <v-alert
              v-if="!field.subFields || field.subFields.length === 0"
              type="info"
              outlined
              dense
            >
              No sub fields defined. Add at least one sub field to collect structured data.
            </v-alert>

            <v-expansion-panels v-if="field.subFields && field.subFields.length > 0">
              <v-expansion-panel
                v-for="(subField, index) in field.subFields"
                :key="index"
              >
                <v-expansion-panel-header>
                  <div class="d-flex align-center">
                    <span>{{ subField.label || `Sub Field ${index + 1}` }}</span>
                    <v-chip
                      x-small
                      color="primary"
                      class="ml-2"
                    >
                      {{ subField.fieldType }}
                    </v-chip>
                    <v-spacer></v-spacer>
                    <v-btn
                      icon
                      x-small
                      color="error"
                      @click.stop="removeSubField(index)"
                    >
                      <v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field
                    v-model="subField.name"
                    label="Field Name"
                    :rules="[v => !!v || 'Name is required']"
                    required
                  ></v-text-field>

                  <v-text-field
                    v-model="subField.label"
                    label="Field Label"
                    :rules="[v => !!v || 'Label is required']"
                    required
                  ></v-text-field>

                  <v-select
                    v-model="subField.fieldType"
                    :items="subFieldTypes"
                    label="Field Type"
                    :rules="[v => !!v || 'Field type is required']"
                    required
                  ></v-select>

                  <v-switch
                    v-model="subField.required"
                    label="Required"
                    color="primary"
                  ></v-switch>

                  <!-- Sub field type specific options -->
                  <template v-if="subField.fieldType === FieldType.TEXT || subField.fieldType === FieldType.TEXTAREA">
                    <v-text-field
                      v-model.number="subField.minLength"
                      label="Minimum Length"
                      type="number"
                    ></v-text-field>
                    <v-text-field
                      v-model.number="subField.maxLength"
                      label="Maximum Length"
                      type="number"
                    ></v-text-field>
                  </template>

                  <template v-if="subField.fieldType === FieldType.NUMBER">
                    <v-text-field
                      v-model.number="subField.minValue"
                      label="Minimum Value"
                      type="number"
                    ></v-text-field>
                    <v-text-field
                      v-model.number="subField.maxValue"
                      label="Maximum Value"
                      type="number"
                    ></v-text-field>
                    <v-text-field
                      v-model.number="subField.stepValue"
                      label="Step Value"
                      type="number"
                    ></v-text-field>
                  </template>

                  <template v-if="subField.fieldType === FieldType.RATING">
                    <v-text-field
                      v-model.number="subField.minRating"
                      label="Minimum Rating"
                      type="number"
                    ></v-text-field>
                    <v-text-field
                      v-model.number="subField.maxRating"
                      label="Maximum Rating"
                      type="number"
                    ></v-text-field>
                  </template>

                  <template v-if="subField.fieldType === FieldType.DATE">
                    <v-text-field
                      v-model="subField.minDate"
                      label="Minimum Date"
                      type="date"
                    ></v-text-field>
                    <v-text-field
                      v-model="subField.maxDate"
                      label="Maximum Date"
                      type="date"
                    ></v-text-field>
                  </template>

                  <template v-if="[FieldType.SINGLE_SELECT, FieldType.MULTI_SELECT, FieldType.CHECKBOX, FieldType.RADIO].includes(subField.fieldType)">
                    <div class="d-flex align-center mb-2">
                      <div class="text-subtitle-2">Options</div>
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        small
                        color="primary"
                        @click="addSubFieldOption(subField)"
                      >
                        <v-icon small left>mdi-plus</v-icon>
                        Add Option
                      </v-btn>
                    </div>
                    <v-list dense>
                      <v-list-item
                        v-for="(option, optionIndex) in subField.options"
                        :key="optionIndex"
                      >
                        <v-list-item-content>
                          <div class="d-flex align-center">
                            <v-text-field
                              v-model="option.label"
                              label="Label"
                              dense
                              class="mr-2"
                            ></v-text-field>
                            <v-text-field
                              v-model="option.value"
                              label="Value"
                              dense
                              class="mr-2"
                            ></v-text-field>
                            <v-btn
                              icon
                              x-small
                              color="error"
                              @click="removeSubFieldOption(subField, optionIndex)"
                            >
                              <v-icon>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                        </v-list-item-content>
                      </v-list-item>
                    </v-list>
                  </template>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="close">Cancel</v-btn>
        <v-btn color="primary" text @click="save" :loading="loading">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { FieldType, PredefinedFieldType } from '@/shared/types/aperTemplate';
import Roles from '@/shared/Roles';

export default {
  name: 'FieldDialog',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    editingField: {
      type: Object,
      default: null
    },
    prefillField: {
      type: Object,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    sections: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      valid: true,
      FieldType: FieldType,
      PredefinedFieldType: PredefinedFieldType,
      field: this.getDefaultField(),
      roles: [
        { label: 'Supervisor', value: Roles.Supervisor },
        { label: 'HOD', value: Roles.Hod },
        { label: 'Dean', value: Roles.Dean },
        { label: 'External Examiner', value: Roles.ExternalExaminer },
        { label: 'Internal Examiner', value: Roles.InternalExaminer },
        { label: 'Reviewer', value: Roles.Reviewer },
        { label: 'PG Coordinator', value: Roles.PgCoordinator },
        { label: 'Dean SPGS', value: Roles.DeanSpgs },
        { label: 'Registrar', value: Roles.Registrar },
        { label: 'VC', value: Roles.VC }
      ]
    }
  },
  computed: {
    isEditing() {
      return !!this.editingField;
    },
    show: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    fieldCodeRules() {
      return [
        v => !v || /^[a-zA-Z][a-zA-Z0-9_]*$/.test(v) || 'Field code must start with a letter and contain only letters, numbers, and underscores',
        v => !v || v.length <= 50 || 'Field code must be 50 characters or less'
      ];
    },
    fieldCodeHint() {
      return 'Unique code for document templates. Use in Word docs as {{ fieldCode }}. Only letters, numbers, and underscores.';
    },
    subFieldTypes() {
      // Exclude complex/special field types from subfield options
      return Object.values(FieldType).filter(
        type => type !== FieldType.MULTI_FIELD &&
                type !== FieldType.MULTI_TEXT &&
                type !== FieldType.SIGNATURE &&
                type !== FieldType.PREDEFINED &&
                type !== FieldType.SECTION_SCORE
      );
    }
  },
  watch: {
    editingField(newVal) {
      if (newVal) {
        // Merge with defaults to ensure new properties like fieldCode are present
        this.field = { ...this.getDefaultField(), ...JSON.parse(JSON.stringify(newVal)) };
      } else if (this.prefillField) {
        this.field = { ...this.getDefaultField(), ...JSON.parse(JSON.stringify(this.prefillField)) };
      } else {
        this.field = this.getDefaultField();
      }
    },
    prefillField(newVal) {
      if (newVal && !this.editingField) {
        this.field = { ...this.getDefaultField(), ...JSON.parse(JSON.stringify(newVal)) };
      }
    },
    value(newVal) {
      if (newVal && this.editingField) {
        // Merge with defaults to ensure new properties like fieldCode are present
        this.field = { ...this.getDefaultField(), ...JSON.parse(JSON.stringify(this.editingField)) };
      } else if (newVal && this.prefillField) {
        this.field = { ...this.getDefaultField(), ...JSON.parse(JSON.stringify(this.prefillField)) };
      } else if (newVal) {
        this.field = this.getDefaultField();
      }
    },
    'field.includeInScoring'(newVal) {
      if (newVal && this.field.useAggregate) {
        this.field.useAggregate = false;
      }
    },
    'field.useAggregate'(newVal) {
      if (newVal && this.field.includeInScoring) {
        this.field.includeInScoring = false;
      }
    }
  },
  methods: {
    onVisibilityChange(visible) {
      if (!visible) {
        this.resetForm();
      }
    },
    getDefaultField() {
      return {
        name: '',
        label: '',
        fieldCode: '',
        displayOrder: 0,
        fieldType: FieldType.TEXT,
        required: false,
        role: null,
        options: [],
        textTemplates: [],
        subFields: [],
        showInReport: true,
        reportOrder: 0,
        includeInScoring: false,
        maximumScore: null,
        useAggregate: false,
        predefinedFieldType: null,
        scoreSectionId: null
      };
    },
    close() {
      this.show = false;
    },
    resetForm() {
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.reset();
      });
    },
    save() {
      if (!this.$refs.form || !this.$refs.form.validate()) return;

      // Create a deep copy of the field object to ensure all nested properties are included
      const fieldData = JSON.parse(JSON.stringify(this.field));
      this.$emit('save', fieldData);
    },
    addOption() {
      if (!this.field.options) {
        this.field.options = [];
      }
      this.field.options.push({
        value: '',
        label: '',
        score: 0,
        displayOrder: this.field.options.length,
        requiredAggregate: null
      });
    },
    removeOption(index) {
      if (this.field.options) this.field.options.splice(index, 1);
    },
    addTextField() {
      if (!this.field.textTemplates) {
        this.field.textTemplates = [];
      }
      this.field.textTemplates.push({
        label: '',
        placeholder: '',
        required: false,
        displayOrder: this.field.textTemplates.length
      });
    },
    removeTextField(index) {
      if (this.field.textTemplates) this.field.textTemplates.splice(index, 1);
    },
    addSubField() {
      if (!this.field.subFields) {
        this.field.subFields = [];
      }

      this.field.subFields.push({
        name: `subfield_${this.field.subFields.length + 1}`,
        label: `Sub Field ${this.field.subFields.length + 1}`,
        fieldType: FieldType.TEXT,
        required: false,
        displayOrder: this.field.subFields.length
      });
    },
    removeSubField(index) {
      if (this.field.subFields) this.field.subFields.splice(index, 1);
    },
    addSubFieldOption(subField) {
      if (!subField.options) {
        subField.options = [];
      }

      subField.options.push({
        value: `option_${subField.options.length + 1}`,
        label: `Option ${subField.options.length + 1}`,
        displayOrder: subField.options.length
      });
    },
    removeSubFieldOption(subField, index) {
      if (subField.options) subField.options.splice(index, 1);
    }
  }
}
</script>
